// deps
import { useCallback, useContext, useMemo } from "react";

// internal-deps
import {
    IPreferredPaymentGroup,
    PreferredGroupActions,
    PreferredGroupSpecificProps,
    PreferredSpecificActions,
} from "@swiggy-private/payment-ui";
import { VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-core";
import { UserContext, getUserFromUserContextValue } from "@swiggy-private/checkout-core";

// interfaces
import { PreferredGroupHandlerProps } from "@payment-client/components/show-payments/components/payment-group-handler/interfaces";

// hooks
import { usePaymentActions } from "./UsePaymentActions";
import { useCardHandler } from "./UseCardHandler";
import { useCredPayHandler } from "./UseCredPayHandler";
import { useWalletHandler } from "./UseWalletHandler";
import { useNetBankingHandler } from "./UseNetBankingHandler";
import { usePayLaterHandler } from "./UsePayLaterHandler";
import { useUPIHandler } from "./UseUPIHandler";
import { useSwiggyInstrumentsHandler } from "./UseSwiggyInstrumentsIHandler";
import { useRequestForBillImage } from "@payment-client/hooks/UseRequestForBillImage";

// helpers
import { canRenderWallet, isWalletPaymentMethod } from "@payment-client/helpers/WalletHelper";
import { isUpiIntentPaymentMethod } from "@payment-client/helpers/UpiHelper";

// context
import { PaymentGroupShowMoreCtaContext } from "@payment-client/contexts/PaymentGroupShowMoreCtaContext";
import useCodHandler from "./useCodHandler";
import { SCREEN_NAME } from "@payment-client/analytics/constants";

export interface UsePreferredHandlerReturnType extends PreferredGroupSpecificProps {
    paymentActions: PreferredGroupActions;
    paymentData: IPreferredPaymentGroup["paymentData"];
}

export const usePreferredHandler = (props: PreferredGroupHandlerProps): UsePreferredHandlerReturnType => {
    const { onPay, onSelect, paymentGroup } = props;
    const paymentActions = usePaymentActions({
        onPay,
        onSelect,
    });

    const { requestForBillImage } = useRequestForBillImage();

    const credEligibleData = useCredPayHandler();
    const { cardSpecificActions } = useCardHandler(props);
    const { walletSpecificActions } = useWalletHandler(props);
    const { netBankingSpecificActions } = useNetBankingHandler(props);
    const { payLaterSpecificActions } = usePayLaterHandler(props);
    const { upiSpecificActions, availableUpiIntentMethods } = useUPIHandler(props);
    const { swiggyInstrumentsSpecificActions } = useSwiggyInstrumentsHandler(props);
    const { user: userContextValue } = useContext(UserContext);
    const { paymentGroupShowMoreCtaState, setPaymentGroupShowMoreCtaState } =
        useContext(PaymentGroupShowMoreCtaContext);
    const { codSpecificActions } = useCodHandler(SCREEN_NAME.PAYMENT);
    const showPreferredMoreCta = useMemo(
        () => paymentGroupShowMoreCtaState[VALID_PAYMENT_GROUP_TYPES.PREFERRED],
        [paymentGroupShowMoreCtaState],
    );
    const user = useMemo(() => getUserFromUserContextValue(userContextValue), [userContextValue]);

    const paymentMethods = paymentGroup.paymentMethods;
    const paymentMethodsToRender = useMemo(
        () =>
            paymentMethods.filter((paymentMethod) => {
                // Fix: example: if backend sends only googlepay upi intent method and google pay is not installed
                // and if that is the only ppm available, the ppm section is rendered empty with just the title.
                if (isUpiIntentPaymentMethod(paymentMethod)) {
                    return availableUpiIntentMethods.includes(paymentMethod.name);
                }
                return !isWalletPaymentMethod(paymentMethod) || canRenderWallet(userContextValue, paymentMethod);
            }),
        [availableUpiIntentMethods, paymentMethods, userContextValue],
    );

    const paymentGroupToRender = useMemo(
        () => ({ ...props.paymentGroup, paymentMethods: paymentMethodsToRender }),
        [props.paymentGroup, paymentMethodsToRender],
    );

    const getPreferredSpecificActions = useCallback(
        (groupName: string): PreferredSpecificActions => {
            switch (groupName) {
                case VALID_PAYMENT_GROUP_TYPES.CARD:
                case VALID_PAYMENT_GROUP_TYPES.FOOD_CARD:
                    return cardSpecificActions;

                case VALID_PAYMENT_GROUP_TYPES.WALLET:
                case VALID_PAYMENT_GROUP_TYPES.WALLET_WEB:
                    return walletSpecificActions;

                case VALID_PAYMENT_GROUP_TYPES.NETBANKING:
                    return netBankingSpecificActions;

                case VALID_PAYMENT_GROUP_TYPES.UPI:
                    return upiSpecificActions;

                case VALID_PAYMENT_GROUP_TYPES.PAY_LATER:
                    return payLaterSpecificActions;

                case VALID_PAYMENT_GROUP_TYPES.SWIGGY_INSTRUMENTS:
                    return swiggyInstrumentsSpecificActions;

                case VALID_PAYMENT_GROUP_TYPES.COD:
                    return codSpecificActions;

                case VALID_PAYMENT_GROUP_TYPES.BILL_TO_COMPANY:
                    return { requestForBillImage };

                default:
                    return undefined;
            }
        },
        [
            cardSpecificActions,
            netBankingSpecificActions,
            payLaterSpecificActions,
            swiggyInstrumentsSpecificActions,
            upiSpecificActions,
            walletSpecificActions,
            codSpecificActions,
            requestForBillImage,
        ],
    );

    const onPreferredMoreCtaClick = useCallback(() => {
        setPaymentGroupShowMoreCtaState(VALID_PAYMENT_GROUP_TYPES.PREFERRED, false);
    }, [setPaymentGroupShowMoreCtaState]);

    return {
        paymentActions,
        getPreferredSpecificActions,
        availableUpiIntentMethods,
        showPreferredMoreCta,
        onPreferredMoreCtaClick,
        user,
        credEligibleData,
        paymentData: paymentGroupToRender,
    };
};
